import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { ErrorState } from '@/components/shared/ErrorState'
import type { RegulatoryExposureBucket } from '@/hooks/compare'

const tooltipStyle = {
  background: 'var(--surface-elevated)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  color: 'var(--text-primary)',
  fontSize: 12,
} as const

function isOtherBucket(
  bucket: Pick<RegulatoryExposureBucket, 'sector' | 'label'>,
) {
  return (
    bucket.sector.trim().toLowerCase() === 'other' ||
    bucket.label.trim().toLowerCase() === 'other'
  )
}

export interface RegulatoryExposureBySectorProps {
  data: RegulatoryExposureBucket[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

export function RegulatoryExposureBySector({
  data,
  isLoading,
  isError,
  onRetry,
}: RegulatoryExposureBySectorProps) {
  const [includeOther, setIncludeOther] = useState(false)
  const hasOtherBucket = data.some(isOtherBucket)
  const hasOtherData = data.some(
    (bucket) => isOtherBucket(bucket) && bucket.articleCount > 0,
  )
  const visibleData = useMemo(
    () => (includeOther ? data : data.filter((bucket) => !isOtherBucket(bucket))),
    [data, includeOther],
  )

  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Regulatory Exposure by Sector</h3>
        <Skeleton className="h-[240px] w-full" />
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Regulatory Exposure by Sector</h3>
        <ErrorState
          title="Couldn't load regulatory exposure data."
          onRetry={onRetry}
        />
      </Card>
    )
  }

  if (visibleData.length === 0) {
    return (
      <Card data-testid="compare-regulatory-exposure" className="p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 className="text-sm font-semibold">Regulatory Exposure by Sector</h3>
          {hasOtherBucket ? (
            <div className="flex items-center gap-2">
              <Switch
                id="regulatory-sector-include-other"
                checked={includeOther}
                onCheckedChange={setIncludeOther}
              />
              <Label
                htmlFor="regulatory-sector-include-other"
                className="text-xs text-muted-foreground"
              >
                Include Other
              </Label>
            </div>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          {hasOtherData && !includeOther
            ? 'Only Other has regulatory coverage data for the selected period. Include Other to view it.'
            : 'No regulatory coverage data for the selected period.'}
        </p>
        <div className="mt-3 text-right">
          <Link
            to="/regulatory"
            className="text-xs text-primary underline-offset-4 hover:underline"
          >
            View enforcement actions →
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <Card data-testid="compare-regulatory-exposure" className="p-4">
      <div className="mb-1 flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold">Regulatory Exposure by Sector</h3>
        {hasOtherBucket ? (
          <div className="flex items-center gap-2">
            <Switch
              id="regulatory-sector-include-other"
              checked={includeOther}
              onCheckedChange={setIncludeOther}
            />
            <Label
              htmlFor="regulatory-sector-include-other"
              className="text-xs text-muted-foreground"
            >
              Include Other
            </Label>
          </div>
        ) : null}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Articles carrying a jurisdiction tag, grouped by sector — across all IntelHub stories; the sector picker does not filter this chart.
      </p>
      <div className="h-[200px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={visibleData}
            layout="vertical"
            margin={{ top: 0, right: 48, left: 8, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--chart-grid-stroke)"
            />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: 'var(--chart-axis-label)' }}
              stroke="var(--chart-axis-stroke)"
              allowDecimals={false}
            />
            <YAxis
              dataKey="label"
              type="category"
              tick={{ fontSize: 10, fill: 'var(--chart-axis-label)' }}
              stroke="var(--chart-axis-stroke)"
              width={100}
            />
            <Tooltip
              cursor={{ fill: 'var(--chart-muted)', opacity: 0.1 }}
              contentStyle={tooltipStyle}
              formatter={(
                value: number,
                _name: string,
                entry: { payload?: RegulatoryExposureBucket },
              ) => [
                `${value} articles (${entry.payload?.pctOfCorpus ?? 0}% of corpus)`,
                'Regulatory exposure',
              ]}
            />
            <Bar dataKey="articleCount" fill="var(--cat-4)" radius={[0, 2, 2, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 text-right">
        <Link
          to="/regulatory"
          className="text-xs text-primary underline-offset-4 hover:underline"
        >
          View enforcement actions →
        </Link>
      </div>
    </Card>
  )
}
